'use client'

import { Children, useEffect, useRef, useState, TouchEvent, ReactNode } from 'react'
import { motion } from 'framer-motion'

type Props = {
    children: ReactNode
    onMeasured?: (height: number) => void
}

const AboutPageCarousel = ({ children, onMeasured }: Props) => {
    const [currentItem, setCurrentItem] = useState(0)
    const [wrapperHeight, setWrapperHeight] = useState(0)
    const contentRef = useRef<HTMLDivElement | null>(null)
    const swipeBaseRef = useRef(0)
    const swipePeekRef = useRef(0)
    const length = Children.toArray(children).length

    useEffect(() => {
        if (!contentRef.current || !length) return

        const itemNodes = contentRef.current.childNodes as NodeListOf<HTMLElement>
        let tallest = 0

        itemNodes.forEach((node) => {
            const h = node.offsetHeight
            if (h > tallest) tallest = h
        })

        setWrapperHeight(tallest)
        onMeasured?.(tallest)
    }, [length, onMeasured])

    const snapToItem = (index: number) => {
        if (!contentRef.current) return
        const newX = -(index / length) * 100
        contentRef.current.style.transform = `translate3d(${newX}%, 0, 0)`
        contentRef.current.style.transitionDuration = ''
        setCurrentItem(index)
    }

    const onTouchStart = (e: TouchEvent) => {
        swipeBaseRef.current = e.touches[0].pageX
    }

    const onTouchMove = (e: TouchEvent) => {
        if (!contentRef.current) return
        const dist = e.touches[0].pageX - swipeBaseRef.current
        if (Math.abs(dist) < 10) return

        const baseX = -(currentItem / length)
        const peek = (dist / window.innerWidth) * 0.25
        swipePeekRef.current = dist
        contentRef.current.style.transform = `translate3d(${(baseX + peek) * 100}%, 0, 0)`
        contentRef.current.style.transitionDuration = '0s'
    }

    const onTouchEnd = () => {
        if (!swipePeekRef.current) return
        if (Math.abs(swipePeekRef.current) >= 100) {
            const next = currentItem + (swipePeekRef.current < 0 ? 1 : -1)
            snapToItem(Math.max(0, Math.min(length - 1, next)))
        } else {
            snapToItem(currentItem)
        }
    }

    return (
        <div className="overflow-hidden py-2">
            <div
                className="relative w-full"
                style={{ height: wrapperHeight }}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
            >
                <motion.div
                    ref={contentRef}
                    className="absolute top-0 left-1/2 flex flex-nowrap transition-transform duration-300 ease-out"
                    style={{ transform: `translate3d(${-currentItem * (100 / length)}%, 0, 0)` }}
                >
                    {Children.map(children, (child, idx) => (
                        <div key={idx} className="flex-shrink-0 flex-grow-0 -translate-x-1/2 transform px-2.5">
                            {child}
                        </div>
                    ))}
                </motion.div>
            </div>

            <div className="mx-auto flex max-w-[500px] items-center justify-center px-[30px] py-4">
                {Children.map(children, (_, i) => (
                    <div
                        key={i}
                        onClick={() => snapToItem(i)}
                        className="mx-1 w-full max-w-[2em] cursor-pointer py-4 opacity-70 transition-opacity select-none hover:opacity-100"
                        style={{ opacity: i === currentItem ? 1 : 0.3 }}
                    >
                        <div className="h-0.5 w-full bg-white" />
                    </div>
                ))}
            </div>
        </div>
    )
}

export default AboutPageCarousel
